import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Grid,
  Title,
  Group,
  Tooltip,
  Divider,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import { useProposal } from '../../context/ProposalContext';
import { PROPOSALACTIONS } from '../../reducers/proposalReducer';
import RegenerateScope from '../Buttons/RegenerateScope';
import RegenerateAssumptions from '../Buttons/RegenerateAssumptions';
import FeesSection from './FeesSection';
import SaveProposal from '../Buttons/SaveProposal';
import HierarchicalTextEditor from '@/components/TextEditor/HierarchicalTextEditor';
import { plainTextToHtml, generatedTextToHtml } from '@/utils/textFormatting';

export default function SecondTab() {
  const { t } = useTranslation();
  const { proposal, dispatchProposal } = useProposal();
  const { getAccessTokenSilently } = useAuth0();
  const [ws, setWs] = useState<WebSocket | null>(null);

  const { input, scope, assumptions, fees, language } = proposal;

  // Convert plain text content to HTML for the editors
  const [scopeHtml, setScopeHtml] = useState<string>('');
  const [assumptionsHtml, setAssumptionsHtml] = useState<string>('');

  // Initialize HTML content from scope/assumptions
  useEffect(() => {
    if (scope && !scopeHtml) {
      // Check if content is already HTML or plain text
      if (scope.includes('<') && scope.includes('>')) {
        // Already HTML, use as-is
        setScopeHtml(scope);
      } else {
        // Plain text, convert to HTML
        setScopeHtml(plainTextToHtml(scope));
      }
    }
  }, [scope, scopeHtml]);

  useEffect(() => {
    if (assumptions && !assumptionsHtml) {
      // Check if content is already HTML or plain text
      if (assumptions.includes('<') && assumptions.includes('>')) {
        // Already HTML, use as-is
        setAssumptionsHtml(assumptions);
      } else {
        // Plain text, convert to HTML
        setAssumptionsHtml(plainTextToHtml(assumptions));
      }
    }
  }, [assumptions, assumptionsHtml]);

  // Listen for generated content updates (from LLM generation)
  useEffect(() => {
    // When scope is updated from generation, check if it's different from current content
    if (scope && scope !== scopeHtml) {
      const htmlContent = (scope.includes('<') && scope.includes('>'))
        ? scope
        : generatedTextToHtml(scope);

      // Update editor and persist HTML into proposal state for DOCX generation
      setScopeHtml(htmlContent);
      dispatchProposal({
        type: PROPOSALACTIONS.SET_FIELD,
        field: 'scope',
        value: htmlContent,
      });
    }
  }, [scope]);

  useEffect(() => {
    // When assumptions are updated from generation, check if it's different from current content  
    if (assumptions && assumptions !== assumptionsHtml) {
      const htmlContent = (assumptions.includes('<') && assumptions.includes('>'))
        ? assumptions
        : generatedTextToHtml(assumptions);

      // Update editor and persist HTML into proposal state for DOCX generation
      setAssumptionsHtml(htmlContent);
      dispatchProposal({
        type: PROPOSALACTIONS.SET_FIELD,
        field: 'assumptions',
        value: htmlContent,
      });
    }
  }, [assumptions]);

  const handleScopeUpdate = (html: string) => {
    setScopeHtml(html);
    // Send HTML directly to backend - no lossy conversion
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'scope',
      value: html, // Send HTML directly instead of converting to plain text
    });
  };

  const handleAssumptionsUpdate = (html: string) => {
    setAssumptionsHtml(html);
    // Send HTML directly to backend - no lossy conversion  
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'assumptions',
      value: html, // Send HTML directly instead of converting to plain text
    });
  };

  return (
    <Stack gap="0" p="md" w="100%">
      <Grid columns={13}>
        {/* Left Column - Scope */}
        <Grid.Col span={6}>
          <Stack>
            {/* Scope of Work */}
            <Group justify="space-between" align="center">
              <Title order={4} mt="lg" ml="sm" mb="2px">
                {t('scopeOfWork')}
              </Title>
              <RegenerateScope />
            </Group>
            <HierarchicalTextEditor
              content={scopeHtml}
              onUpdate={handleScopeUpdate}
              placeholder={t('addScopeOfWorkHere')}
              minHeight={300}
            />
          </Stack>
        </Grid.Col>

        {/* Right Column - Assumptions */}
        <Grid.Col span={6} offset={1}>
          {/* Assumptions */}
          <Group justify="space-between" align="center">
            <Group gap="xs" align="center" mt="lg">
              <Title order={4} ml="sm" mb="2px">
                {t('assumptions')}
              </Title>
              <Tooltip label={t('assumptionsTooltip')} position="top" withArrow maw={300} multiline>
                <IconInfoCircle size={18} color="#228be6" style={{ cursor: 'help' }} />
              </Tooltip>
            </Group>
            <RegenerateAssumptions />
          </Group>
          <HierarchicalTextEditor
            content={assumptionsHtml}
            onUpdate={handleAssumptionsUpdate}
            placeholder={t('addAssumptionsHere')}
            minHeight={300}
          />
        </Grid.Col>
      </Grid>
      <Divider mt="xl" />
      <Stack>
        <FeesSection fees={fees} />
      </Stack>
      <Group justify="flex-end" mt="md">
        <SaveProposal />
      </Group>
    </Stack>
  );
}
